import {useState, useEffect} from 'react'
import {useParams, useNavigate} from 'react-router-dom'
import {Card, Table, Spinner, Modal, Button} from 'react-bootstrap'
import {useDeleteVoiceMutation, useGetRawContactDetailQuery} from '../../../../services/cellcast.v2'
import {ContactData} from '../../../../services/cellcast.v2.types'
import {useVoiceRecorder} from './hooks/useVoiceRecorder'
import {VoiceAnalysisResponse, VoiceItem} from './type'
import {KTSVG} from '../../../../_metronic/helpers'
import Pagination from '../../../Components/shared/data-table/pagination'
import {useI18n} from '../../../Utils/_helpers/useI18n'
import {useResponsive} from '../../../hooks/useResponsive'
import './RecordList.scss'
import DeleteConfirmationPopup from '../../../Components/DeleteConfirmationPopup'
import EditMessageModal from './components/EditMessageModal'
import VoiceRecordModal from './VoiceRecordModal'
import VoiceRecordMobileCard from './components/VoiceRecordMobileCard'
import {Contact} from './type'

const RecordList = () => {
  const {id} = useParams<{id: string}>()
  const navigate = useNavigate()
  const {t} = useI18n()
  const {isMobile} = useResponsive()
  const {data: rawContactData} = useGetRawContactDetailQuery({id: id || ''})
  const contact = rawContactData?.data as ContactData
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const {recordList, isLoading, refetch} = useVoiceRecorder({
    rawContactId: id || '',
    page: currentPage,
    limit: pageSize,
  })

  // Always refetch latest records when landing on this page or id changes
  useEffect(() => {
    if (id) {
      refetch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const voiceRecords = recordList?.data as VoiceAnalysisResponse
  const [deleteVoice] = useDeleteVoiceMutation()
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [recordToDelete, setRecordToDelete] = useState<VoiceItem | null>(null)

  // Modal state for transcript and summary popup
  const [showTranscriptModal, setShowTranscriptModal] = useState(false)
  const [showSummaryModal, setShowSummaryModal] = useState(false)
  const [showActionItemsModal, setShowActionItemsModal] = useState(false)
  const [showOpenersModal, setShowOpenersModal] = useState(false)
  const [showWelcomeMessageModal, setShowWelcomeMessageModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showVoiceModal, setShowVoiceModal] = useState(false)
  const [selectedTranscript, setSelectedTranscript] = useState('')
  const [selectedSummary, setSelectedSummary] = useState('')
  const [selectedWelcomeMessage, setSelectedWelcomeMessage] = useState('')
  const [selectedActionItems, setSelectedActionItems] = useState<string[]>([])
  const [selectedOpeners, setSelectedOpeners] = useState<string[]>([])
  const [modalTitle, setModalTitle] = useState('')
  const [selectedRecord, setSelectedRecord] = useState<VoiceItem | null>(null)

  const handleShowTranscript = (transcript: string) => {
    setSelectedTranscript(transcript)
    setModalTitle(t('VOICE_RECORDS.MODAL.TRANSCRIPT_TITLE'))
    setShowTranscriptModal(true)
  }

  const handleShowSummary = (summary: string) => {
    setSelectedSummary(summary)
    setModalTitle(t('VOICE_RECORDS.MODAL.SUMMARY_TITLE'))
    setShowSummaryModal(true)
  }

  const handleShowWelcomeMessage = (welcomeMessage: string, record?: VoiceItem) => {
    setSelectedWelcomeMessage(welcomeMessage)
    setModalTitle(t('VOICE_RECORDS.MODAL.WELCOME_MESSAGE_TITLE'))
    if (record) setSelectedRecord(record)
    setShowWelcomeMessageModal(true)
  }

  const handleShowActionItems = (actionItems: string[]) => {
    setSelectedActionItems(actionItems)
    setModalTitle(t('VOICE_RECORDS.MODAL.ACTION_ITEMS_TITLE'))
    setShowActionItemsModal(true)
  }

  const handleShowOpeners = (openers: string[], record?: VoiceItem) => {
    setSelectedOpeners(openers)
    setModalTitle(t('VOICE_RECORDS.MODAL.OPENERS_TITLE'))
    if (record) setSelectedRecord(record)
    setShowOpenersModal(true)
  }

  const handleOpenDeletePopup = (record: VoiceItem) => {
    setRecordToDelete(record)
    setShowDeleteConfirmation(true)
  }

  const handleDownloadTranscript = (record: VoiceItem) => {
    if (!record.transcript) return

    // Extract filename from voiceFile URL, clean it up, and replace extension with .txt
    let fileName = 'transcript.txt'
    if (record.voiceFile) {
      const urlPath = record.voiceFile.split('/').pop()?.split('?')[0] || ''
      let baseName = urlPath.replace(/\.[^/.]+$/, '') // remove extension
      baseName = decodeURIComponent(baseName) // decode %20 etc.
      baseName = baseName.replace(/^\d{10,}-/, '') // remove leading timestamp prefix
      baseName = baseName.trim()
      if (baseName) {
        fileName = `${baseName}.txt`
      }
    }

    const blob = new Blob([record.transcript], {type: 'text/plain;charset=utf-8'})
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const handleConfirmDelete = async () => {
    if (!recordToDelete?.id) return
    try {
      setDeletingId(recordToDelete.id)
      await deleteVoice({id: recordToDelete.id}).unwrap()
      await refetch()
    } catch (e) {
      console.error('Failed to delete voice record', e)
    } finally {
      setDeletingId(null)
      setRecordToDelete(null)
      setShowDeleteConfirmation(false)
    }
  }

  const handleCloseTranscriptModal = () => {
    setShowTranscriptModal(false)
    setSelectedTranscript('')
    setModalTitle('')
  }

  const handleCloseSummaryModal = () => {
    setShowSummaryModal(false)
    setSelectedSummary('')
    setModalTitle('')
  }
  const handleCloseWelcomeMessageModal = () => {
    setShowWelcomeMessageModal(false)
    setSelectedWelcomeMessage('')
    setModalTitle('')
    setSelectedRecord(null)
  }

  const handleCloseActionItemsModal = () => {
    setShowActionItemsModal(false)
    setSelectedActionItems([])
    setModalTitle('')
  }

  const handleCloseOpenersModal = () => {
    setShowOpenersModal(false)
    setSelectedOpeners([])
    setModalTitle('')
    setSelectedRecord(null)
  }

  const handleOpenEditModal = (record: VoiceItem) => {
    setSelectedRecord(record)
    setShowEditModal(true)
  }

  const handleCloseEditModal = () => {
    setShowEditModal(false)
    setSelectedRecord(null)
  }

  const handlePageChange = (selectedPage: number) => {
    setCurrentPage(selectedPage + 1) // ReactPaginate uses 0-based indexing
  }

  const handleLimitChange = (newLimit: number) => {
    setPageSize(newLimit)
    setCurrentPage(1)
  }

  const renderStatusBadge = (status: string) => {
    let badgeClass = 'badge status-badge'
    let statusText = status

    switch (status) {
      case 'completed':
        badgeClass = 'badge status-badge completed'
        break
      case 'pending':
        badgeClass = 'badge status-badge pending'
        break
      case 'failed':
        badgeClass = 'badge status-badge failed'
        break
      default:
        badgeClass = 'badge status-badge'
    }

    return <span className={`${badgeClass} fw-bold`}>{statusText}</span>
  }

  const renderSentimentBadge = (sentiment: string) => {
    let badgeClass = 'badge sentiment-badge'

    switch (sentiment?.toLowerCase()) {
      case 'positive':
        badgeClass = 'badge sentiment-badge positive'
        break
      case 'negative':
        badgeClass = 'badge sentiment-badge negative'
        break
      case 'neutral':
        badgeClass = 'badge sentiment-badge neutral'
        break
      default:
        badgeClass = 'badge sentiment-badge'
    }

    return <span className={`${badgeClass} fw-bold`}>{sentiment}</span>
  }

  const renderTruncatedText = (
    text: string,
    type: 'transcript' | 'summary' | 'welcomeMessage',
    record?: VoiceItem
  ) => {
    if (!text) return <span className='text-muted'>-</span>

    const limit = 150
    const isLongText = text.length - limit > 5
    const displayText = isLongText ? text.slice(0, limit) + '...' : text

    return (
      <div className={`${type}-cell`}>
        <p className={`${type}-text`}>
          {displayText}{' '}
          {isLongText && (
            <span
              className='see-more-btn'
              onClick={() =>
                type === 'transcript'
                  ? handleShowTranscript(text)
                  : type === 'summary'
                  ? handleShowSummary(text)
                  : type === 'welcomeMessage'
                  ? handleShowWelcomeMessage(text, record)
                  : null
              }
            >
              {t('VOICE_RECORDS.SEE_MORE')}
            </span>
          )}
        </p>
      </div>
    )
  }

  const renderListWithSeeMore = (items: string[], onShowMore: (items: string[]) => void) => {
    if (!items || items.length === 0) {
      return <span className='text-gray-800 fw-bold'>-</span>
    }

    const maxItems = 2
    const hasMoreItems = items.length > maxItems
    const displayItems = hasMoreItems ? items.slice(0, maxItems) : items

    return (
      <div className='list-cell'>
        <ul className='action-items-list mb-0'>
          {displayItems.map((item, index) => (
            <li key={index} className='text-gray-800 fw-bold text-truncate-3-lines' title={item}>
              {item}
            </li>
          ))}
        </ul>
        {hasMoreItems && (
          <span className='see-more-btn' onClick={() => onShowMore(items)}>
            {t('VOICE_RECORDS.SEE_MORE')}
          </span>
        )}
      </div>
    )
  }

  const formatRecordDate = (value: string) => {
    if (!value) return '-'
    const d = new Date(value)
    const dateStr = d.toLocaleDateString()
    const timeStr = d.toLocaleTimeString()
    return `${dateStr} ${timeStr}`
  }

  // Calculate pagination values
  const totalRecords = voiceRecords?.total || 0
  const totalPages = voiceRecords?.totalPages || 1
  // summaryText no longer used: unified pagination component shows range

  return (
    <div className='container-xxl record-list'>
      <Card className='mb-5 contact-info-card'>
        <Card.Header>
          <div className='d-flex align-items-center gap-3 flex-wrap'>
            <span
              className='me-2'
              style={{cursor: 'pointer'}}
              onClick={() => navigate(-1)}
              title={t('COMMON.ACTIONS.BACK')}
            >
              <i className='bi bi-arrow-left fs-4 text-primary'></i>
            </span>
            <h3 className='card-title m-0'>{t('VOICE_RECORDS.CONTACT_INFORMATION_TITLE')}</h3>
          </div>
        </Card.Header>
        <Card.Body>
          <div className='mb-3 contact-info-row'>
            <div className='contact-info-item'>
              <label className='fw-bold'>Name:</label>
              <span>{contact?.fullName || '-'}</span>
            </div>
            <div className='contact-info-item'>
              <label className='fw-bold'>Phone:</label>
              <span>{contact?.phone || '-'}</span>
            </div>
            <div className='contact-info-item'>
              <label className='fw-bold'>Email:</label>
              <span>{contact?.email || '-'}</span>
            </div>
            <div className='contact-info-item'>
              <label className='fw-bold'>Assigned To:</label>
              <span>{(typeof contact?.assignedTo === 'object' ? contact?.assignedTo?.name : null) || '-'}</span>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <div className='d-flex align-items-center justify-content-between w-100'>
            <h3 className='card-title m-0'>{t('VOICE_RECORDS.TITLE')}</h3>
            <Button
              variant='primary'
              onClick={() => setShowVoiceModal(true)}
              className='d-flex align-items-center gap-2 ms-auto'
            >
              <i className='bi bi-mic-fill'></i>
              {t('VOICE_RECORDS.START_RECORDING') || 'Record Voice Note'}
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          <div className='table-container position-relative'>
            {isLoading && (
              <div className='loading-overlay'>
                <div className='loading-spinner-container'>
                  <Spinner animation='border' role='status'>
                    <span className='visually-hidden'>Loading...</span>
                  </Spinner>
                </div>
              </div>
            )}
            {isMobile ? (
              /* Mobile Card View */
              <div className='voice-records-mobile-list'>
                {voiceRecords?.items && voiceRecords.items.length > 0 ? (
                  voiceRecords.items.map((record: VoiceItem) => (
                    <VoiceRecordMobileCard
                      key={record.id}
                      record={record}
                      formatRecordDate={formatRecordDate}
                      renderStatusBadge={renderStatusBadge}
                      renderSentimentBadge={renderSentimentBadge}
                      renderTruncatedText={renderTruncatedText}
                      renderListWithSeeMore={renderListWithSeeMore}
                      handleOpenEditModal={handleOpenEditModal}
                      handleShowOpeners={handleShowOpeners}
                      handleShowActionItems={handleShowActionItems}
                      handleOpenDeletePopup={handleOpenDeletePopup}
                      handleDownloadTranscript={handleDownloadTranscript}
                      handleSendToInbox={() => {
                        if (contact?.phone) {
                          navigate({pathname: '/inbox', search: `?receiver=${contact.phone}`})
                        }
                      }}
                      deletingId={deletingId}
                      contactPhone={contact?.phone}
                    />
                  ))
                ) : (
                  <div className='text-center py-5 text-muted'>
                    {t('VOICE_RECORDS.NO_RECORDS')}
                  </div>
                )}
              </div>
            ) : (
              /* Desktop Table View */
              <div className='table-responsive'>
              <Table
                hover
                className={`voice-records-table table-row-dashed table-row-gray-200 align-middle gs-0 gy-4 ${
                  isLoading ? 'loading' : ''
                }`}
              >
                <thead>
                  <tr className='fw-bold text-muted'>
                    <th className='min-w-150px'>{t('VOICE_RECORDS.TABLE.RECORD_DATE')}</th>
                    <th className='min-w-200px'>{t('VOICE_RECORDS.TABLE.AUDIO')}</th>
                    <th className='min-w-400px'>{t('VOICE_RECORDS.TABLE.TRANSCRIPT')}</th>
                    <th className='min-w-350px'>{t('VOICE_RECORDS.TABLE.SUMMARY')}</th>
                    <th className='min-w-100px'>{t('VOICE_RECORDS.TABLE.STATUS')}</th>
                    <th className='min-w-100px'>{t('VOICE_RECORDS.TABLE.SENTIMENT')}</th>
                    <th className='min-w-100px'>{t('VOICE_RECORDS.TABLE.LEAD_INTENT')}</th>
                    <th className='min-w-350px'>{t('VOICE_RECORDS.TABLE.WELCOME_MESSAGE')}</th>
                    <th className='min-w-350px'>{t('VOICE_RECORDS.TABLE.OPENERS')}</th>
                    <th className='min-w-350px'>{t('VOICE_RECORDS.TABLE.ACTION_ITEMS')}</th>
                    <th className='min-w-100px'>{t('VOICE_RECORDS.TABLE.ACTIONS')}</th>
                  </tr>
                </thead>
                <tbody>
                  {voiceRecords?.items && voiceRecords.items.length > 0 ? (
                    voiceRecords.items.map((record: VoiceItem) => (
                      <tr key={record.id}>
                        <td className='border-bottom min-w-170px'>
                          <span className='text-gray-800 fw-bold'>
                            {formatRecordDate(record.createdAt)}
                          </span>
                        </td>
                        <td className='border-bottom audio-cell'>
                          <audio controls src={record.voiceFile}>
                            Your browser does not support the audio element.
                          </audio>
                        </td>
                        <td className='border-bottom'>
                          {renderTruncatedText(record.transcript, 'transcript')}
                        </td>
                        <td className='border-bottom'>
                          {renderTruncatedText(record.summary, 'summary')}
                        </td>
                        <td className='border-bottom'>{renderStatusBadge(record.status)}</td>
                        <td className='border-bottom'>{renderSentimentBadge(record.sentiment)}</td>
                        <td className='border-bottom'>
                          <span className='text-gray-800 fw-bold'>{record.leadIntent || '-'}</span>
                        </td>
                        <td className='border-bottom'>
                          {renderTruncatedText(record.welcomeMessage, 'welcomeMessage', record)}
                        </td>
                        <td className='border-bottom'>
                          {renderListWithSeeMore(record?.nextCallOpeners || [], (items) =>
                            handleShowOpeners(items, record)
                          )}
                        </td>
                        <td className='border-bottom'>
                          {renderListWithSeeMore(
                            record?.suggestedNextSteps || [],
                            handleShowActionItems
                          )}
                        </td>
                        <td className='border-bottom'>
                          <div className='action-buttons d-flex gap-2'>
                            <span
                              className='btn btn-sm btn-icon btn-light btn-active-light-primary'
                              title='Edit Welcome Message & Openers'
                              onClick={() => handleOpenEditModal(record)}
                            >
                              <i className='bi bi-pencil'></i>
                            </span>
                            <span
                              className='btn btn-sm btn-icon btn-light btn-active-light-primary'
                              title='Send to Inbox'
                              onClick={() => {
                                if (contact?.phone) {
                                  navigate({pathname: '/inbox', search: `?receiver=${contact.phone}`})
                                }
                              }}
                            >
                              <i className='bi bi-send'></i>
                            </span>
                            {record.transcript && (
                              <span
                                className='btn btn-sm btn-icon btn-light btn-active-light-primary'
                                title='Download Transcript'
                                onClick={() => handleDownloadTranscript(record)}
                              >
                                <i className='bi bi-download'></i>
                              </span>
                            )}
                            <span
                              className={`btn btn-sm btn-icon trash btn-light btn-active-light-primary ${
                                deletingId === record.id ? 'disabled' : ''
                              }`}
                              onClick={() =>
                                deletingId !== record.id && handleOpenDeletePopup(record)
                              }
                            >
                              <KTSVG
                                className='svg-icon-2'
                                path='/media/icons/duotune/art/trush.svg'
                              />
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className='text-center py-4'>
                        {t('VOICE_RECORDS.NO_RECORDS')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
            )}

            {/* Pagination */}
            {voiceRecords && voiceRecords.items && voiceRecords.items.length > 0 && (
              <div className='record-pagination'>
                <Pagination
                  limit={pageSize}
                  onPageChange={handlePageChange}
                  onLimitChange={handleLimitChange}
                  isLoading={isLoading}
                  total={totalRecords}
                  totalPages={totalPages}
                  loadedData={{
                    startLoad: (currentPage - 1) * pageSize + 1,
                    endLoad: Math.min(currentPage * pageSize, totalRecords),
                  }}
                />
              </div>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* Transcript Modal */}
      <Modal
        show={showTranscriptModal}
        onHide={handleCloseTranscriptModal}
        size='lg'
        className='transcript-modal'
      >
        <Modal.Header closeButton>
          <Modal.Title className='transcript-modal-title'>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='transcript-content'>
            <p>{selectedTranscript}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleCloseTranscriptModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Summary Modal */}
      <Modal
        show={showSummaryModal}
        onHide={handleCloseSummaryModal}
        size='lg'
        className='transcript-modal'
      >
        <Modal.Header closeButton>
          <Modal.Title className='transcript-modal-title'>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='transcript-content'>
            <p>{selectedSummary}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleCloseSummaryModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Welcome Message Modal */}
      <Modal
        show={showWelcomeMessageModal}
        onHide={handleCloseWelcomeMessageModal}
        size='lg'
        className='transcript-modal'
      >
        <Modal.Header closeButton>
          <Modal.Title className='transcript-modal-title'>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='transcript-content'>
            <p>{selectedWelcomeMessage}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleCloseWelcomeMessageModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Action Items Modal */}
      <Modal
        show={showActionItemsModal}
        onHide={handleCloseActionItemsModal}
        size='lg'
        className='transcript-modal'
      >
        <Modal.Header closeButton>
          <Modal.Title className='transcript-modal-title'>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='transcript-content'>
            <ul className='action-items-full-list'>
              {selectedActionItems.map((item, index) => (
                <li key={index} className='text-gray-800 fw-bold mb-2'>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleCloseActionItemsModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Openers Modal */}
      <Modal
        show={showOpenersModal}
        onHide={handleCloseOpenersModal}
        size='lg'
        className='transcript-modal'
      >
        <Modal.Header closeButton>
          <Modal.Title className='transcript-modal-title'>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='transcript-content'>
            <ul className='action-items-full-list'>
              {selectedOpeners.map((item, index) => (
                <li key={index} className='text-gray-800 fw-bold mb-2'>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleCloseOpenersModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Welcome Message & Openers Modal */}
      <EditMessageModal
        show={showEditModal}
        record={selectedRecord}
        contact={contact}
        onClose={handleCloseEditModal}
        onSaved={refetch}
      />

      {/* Voice Record Modal */}
      {contact && (
        <VoiceRecordModal
          show={showVoiceModal}
          contact={{
            id: contact.id || '',
            fullName: contact.fullName,
            phone: contact.phone,
            email: contact.email || '',
            cardName: '',
            createdAt: '',
            countryCode: '',
          } as Contact}
          onClose={() => setShowVoiceModal(false)}
          onSaved={() => {
            refetch()
            setShowVoiceModal(false)
          }}
        />
      )}

      {/* Delete Confirmation Popup */}
      <DeleteConfirmationPopup
        show={showDeleteConfirmation}
        handleClose={() => {
          setShowDeleteConfirmation(false)
          setRecordToDelete(null)
        }}
        Message={t('VOICE_RECORDS.DELETE.CONFIRM_MESSAGE')}
        confirm={handleConfirmDelete}
        confirmBtnTitle={t('VOICE_RECORDS.DELETE.CONFIRM_BUTTON')}
      />
    </div>
  )
}

export default RecordList
